import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Tabs, Tab, Paper, Button, Grid, Skeleton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';
import { HousekeepingService } from '../../api/services/housekeepingService';

import LiveTasksTab from './LiveTasksTab';
import LostFoundTab from './LostFoundTab';
import ConfigurationTab from './ConfigurationTab';
import PerformanceAnalyticsTab from './PerformanceAnalyticsTab';
import TaskHistoryTab from './TaskHistoryTab';
import TaskFormModal from '../../components/TaskFormModal';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

export default function Housekeeping() {
    const { user } = useSelector(state => state.auth);
    const tenantId = user?.tenantId || '1';

    const userPermissions = useMemo(() => {
        if (!user) return [];
        let rawPerms = [];
        if (Array.isArray(user.customPermissions) && user.customPermissions.length > 0) {
            rawPerms = user.customPermissions;
        } else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
            rawPerms = user.permissions;
        } else if (user.role && Array.isArray(user.role.permissions)) {
            rawPerms = user.role.permissions;
        }

        return rawPerms.map(p => {
            if (typeof p === 'string') return p;
            if (p && typeof p === 'object') {
                return p.key || p.name || p.code || '';
            }
            return '';
        }).filter(Boolean);
    }, [user]);

    const canManage = userPermissions.includes('*') || userPermissions.includes('CAN_MANAGE_HOUSEKEEPING');

    const [tabValue, setTabValue] = useState(0);
    const [stats, setStats] = useState(null);
    const [isLoadingStats, setIsLoadingStats] = useState(true);
    
    const [taskModalOpen, setTaskModalOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const fetchDashboardStats = async () => {
        setIsLoadingStats(true);
        try {
            const res = await HousekeepingService.getDashboardStats(tenantId);
            const data = res?.data || res || {};
            const statusCounts = data.statusCounts || {};
            const totalTasks = Object.values(statusCounts).reduce((acc, count) => acc + count, 0);
            
            setStats({
                totalTasks: totalTasks,
                completed: statusCounts['Completed'] || 0,
                inProgress: statusCounts['In Progress'] || 0,
                overdue: data.overdueTasksCount || 0
            });
        } catch (error) {
            console.error("Failed to fetch dashboard stats", error);
            setStats(null);
        } finally {
            setIsLoadingStats(false);
        }
    };

    useEffect(() => {
        if (tabValue === 0 || tabValue === 4) {
            fetchDashboardStats();
        }
    }, [tabValue, tenantId, refreshKey]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleTaskCreated = () => {
        fetchDashboardStats();
        setRefreshKey(prev => prev + 1);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">Housekeeping Management</Typography>
                {canManage && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setTaskModalOpen(true)} sx={{ borderRadius: 2 }}>
                        Create Task
                    </Button>
                )}
            </Box>

            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', height: '100%' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                            TOTAL ROOMS TO CLEAN
                        </Typography>
                        {isLoadingStats ? (
                            <Skeleton variant="text" width={60} height={40} />
                        ) : (
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '1.85rem', lineHeight: 1.1 }}>
                                {stats?.totalTasks || 0}
                            </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                            Daily assignment total
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', height: '100%' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                            COMPLETED TASKS
                        </Typography>
                        {isLoadingStats ? (
                            <Skeleton variant="text" width={60} height={40} />
                        ) : (
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#059669', fontSize: '1.85rem', lineHeight: 1.1 }}>
                                {stats?.completed || 0}
                            </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                            Inspected and ready
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', height: '100%' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                            IN PROGRESS
                        </Typography>
                        {isLoadingStats ? (
                            <Skeleton variant="text" width={60} height={40} />
                        ) : (
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#4146e0', fontSize: '1.85rem', lineHeight: 1.1 }}>
                                {stats?.inProgress || 0}
                            </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                            Active cleaning underway
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', height: '100%' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                            DELAYED / HIGH PRIORITY
                        </Typography>
                        {isLoadingStats ? (
                            <Skeleton variant="text" width={60} height={40} />
                        ) : (
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#dc2626', fontSize: '1.85rem', lineHeight: 1.1 }}>
                                {stats?.overdue || 0}
                            </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                            Priority attention required
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Box sx={{ borderBottom: 1, borderColor: '#eaecf0', mb: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                    <Tab label="Live Tasks" />
                    <Tab label="Lost & Found" />
                    <Tab label="Configuration" />
                    <Tab label="Performance Analytics" />
                    <Tab label="Task History" />
                </Tabs>
            </Box>

            <Box>
                <TabPanel value={tabValue} index={0}>
                    <LiveTasksTab tenantId={tenantId} refreshKey={refreshKey} onTaskSuccess={handleTaskCreated} />
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                    <LostFoundTab tenantId={tenantId} />
                </TabPanel>
                <TabPanel value={tabValue} index={2}>
                    <ConfigurationTab tenantId={tenantId} />
                </TabPanel>
                <TabPanel value={tabValue} index={3}>
                    <PerformanceAnalyticsTab tenantId={tenantId} />
                </TabPanel>
                <TabPanel value={tabValue} index={4}>
                    <TaskHistoryTab tenantId={tenantId} refreshKey={refreshKey} />
                </TabPanel>
            </Box>

            <TaskFormModal 
                open={taskModalOpen}
                onClose={() => setTaskModalOpen(false)}
                editingTask={null}
                onSuccess={handleTaskCreated}
            />
        </Box>
    );
}
